import PBService from "@irati/utils/services"
import client, {QueryOptions, MutationOptions} from "@irati/utils/services/apollo"
import {
  ReportingTeamState,
  TeamDateInput,
  ReportType,
  TeamReportDuration,
  UserRedemptionReportSavings,
  BusinessRedemptionReport,
  BusinessStory,
  UserSavingsBrand,
  UserSavingsCategories,
  QueryBusinessRedemptionReportArgs,
  QueryOrganisationalSummaryArgs,
  QueryCelebrationsReportArgs,
  QueryOnboardingReportByStepArgs,
  QueryBusinessOverviewArgs,
  QueryBusinessStoryArgs,
  QueryUserSavingsArgs
} from "@irati/types"

import overview, {overviewKey} from "./queries/overview"
import businessOverview, {businessOverviewKey} from "./queries/businessOverview"
import usageReports, {usageReportsKey} from "./queries/usageReports"
import onboardingReport, {onboardingReportKey} from "./queries/onboardingReport"
import teamReports, {teamReportsKey} from "./queries/teamReports"
import businessSettings, {businessSettingsKey} from "./queries/businessSettings"
import updateBusinessSettings, {updateBusinessSettingsKey} from "./mutations/updateBusinessSettings"
import rewardsReportUrl, {rewardsReportUrlKey} from "./queries/rewardsReportUrl"
import businessRedemptionReport, {businessRedemptionReportKey} from "./queries/businessRedemptionReport"
import organisationalSummary, {organisationalSummaryKey} from "./queries/organisationalSummary"
import celebrationsReport, {celebrationsReportKey} from "./queries/celebrationsReport"
import onboardingReportStep, {onboardingReportStepKey} from "./queries/onboardingReportStep"
import userSavings, {userSavingsKey} from "./queries/userSavings"
import businessStory, {businessStoryKey} from "./queries/businessStory"

class Service extends PBService {
  getOverview = (businessId: string, options: QueryOptions = {}) => {
    const variables = {businessId}
    return client.query({...options, query: overview, variables}).then(({data}) => {
      return data[overviewKey]
    })
  }
  getBusinessOverview = (variables: QueryBusinessOverviewArgs, options: QueryOptions = {}) => {
    return client.query({...options, query: businessOverview, variables}).then(({data}) => {
      return data[businessOverviewKey]
    })
  }

  getUsageReports = (businessId: string, topics?: string[], interval?: string, options: QueryOptions = {}) => {
    const variables = {businessId, topics, interval}
    return client.query({...options, query: usageReports, variables}).then(({data}) => {
      return data[usageReportsKey]
    })
  }
  getOnboarding = (businessId: string, options: QueryOptions = {}) => {
    const variables = {businessId}
    return client.query({...options, query: onboardingReport, variables}).then(({data}) => {
      return data[onboardingReportKey]
    })
  }

  getOnboardingStep = (variables: QueryOnboardingReportByStepArgs, options: QueryOptions = {}) => {
    return client.query({...options, query: onboardingReportStep, variables}).then(({data}) => {
      return data[onboardingReportStepKey]
    })
  }

  getTeamReports = (
    businessId: string,
    duration: TeamReportDuration,
    teamStates: ReportingTeamState[],
    teamDate?: TeamDateInput,
    options: QueryOptions = {}
  ) => {
    const variables = {businessId, duration, teamStates, teamDate}
    return client.query({...options, query: teamReports, variables}).then(({data}) => {
      return data[teamReportsKey]
    })
  }

  getBusinessSettings = (businessId: string, options: QueryOptions = {}) => {
    const variables = {businessId}
    return client.query({...options, query: businessSettings, variables}).then(({data}) => {
      return data[businessSettingsKey]
    })
  }

  updateBusinessSettings = (businessId: string, industry: string, showUntil: string, options: MutationOptions = {}) => {
    const variables = {businessId, industry, showUntil}
    return client.mutate({...options, mutation: updateBusinessSettings, variables}).then(({data}) => {
      return data[updateBusinessSettingsKey]
    })
  }

  getRewardsReportUrl = (businessId: string, reportType: ReportType, options: QueryOptions = {}) => {
    const variables = {businessId, reportType}
    return client.query({...options, query: rewardsReportUrl, variables}).then(({data}) => {
      return data[rewardsReportUrlKey]
    })
  }

  getBusinessRedemptionReport = (variables: QueryBusinessRedemptionReportArgs, options: QueryOptions = {}) => {
    return client.query({...options, query: businessRedemptionReport, variables}).then(({data}) => {
      return data[businessRedemptionReportKey]
    })
  }

  getOrganisationalSummary = (variables: QueryOrganisationalSummaryArgs, options: QueryOptions = {}) => {
    return client.query({...options, query: organisationalSummary, variables}).then(({data}) => {
      return data[organisationalSummaryKey]
    })
  }

  getCelebrationsReport = (variables: QueryCelebrationsReportArgs, options: QueryOptions = {}) => {
    return client.query({...options, query: celebrationsReport, variables}).then(({data}) => {
      return data[celebrationsReportKey]
    })
  }

  getUserSavings = async (variables: QueryUserSavingsArgs, options: QueryOptions = {}): Promise<UserSavings> => {
    const {data} = await client.query({...options, query: userSavings, variables})
    return data[userSavingsKey]
  }

  getBusinessStory = async (variables: QueryBusinessStoryArgs, options: QueryOptions = {}): Promise<BusinessStory> => {
    const {data} = await client.query({...options, query: businessStory, variables})
    return data[businessStoryKey]
  }
}

export interface UserSavings {
  report: BusinessRedemptionReport
  savings: UserRedemptionReportSavings
  previousSavings?: UserRedemptionReportSavings
  brands: UserSavingsBrand
  categories: UserSavingsCategories
}

const service = new Service({errorHandlers: []})

export default service
